<script lang="ts">
  import {
    ChevronDown,
    ChevronUp,
    Cloud,
    CloudDrizzle,
    CloudFog,
    CloudLightning,
    CloudMoon,
    CloudRain,
    CloudSnow,
    CloudSun,
    Cloudy,
    Moon,
    Sun,
  } from "lucide-svelte";
  import type { PublicBridge, PublicRoadTraffic, PublicSnapshot } from "../../shared/contracts";
  import { bridgeTrafficCopy, forecastCopy, formatEtTime, freshnessLabel } from "../format";
  import { brickellAvenueTrafficLabel } from "../road-traffic-presentation";

  type WeatherTone = "current" | "expected" | "active" | "unavailable";
  type WeatherIcon =
    | "clear-day"
    | "clear-night"
    | "partly-cloudy-day"
    | "partly-cloudy-night"
    | "cloudy"
    | "fog"
    | "drizzle"
    | "rain"
    | "snow"
    | "thunderstorm"
    | "unknown";

  interface WeatherView {
    tone: WeatherTone;
    icon: WeatherIcon;
    temperature: string;
    status: string;
    detail: string;
    timing: string | null;
    alerting: boolean;
  }

  export let forecast: PublicSnapshot["forecast"];
  export let bridge: PublicBridge;
  export let weather: PublicSnapshot["weather"];
  export let roadTraffic: PublicRoadTraffic = {
    source: "modeled",
    condition: "unavailable",
    congestionPercent: null,
    speedMph: null,
    freeFlowSpeedMph: null,
    observedSegments: 0,
    roads: [],
    freshness: "unknown",
    updatedAt: null,
  };
  export let now: Date;
  export let compact = false;
  export let expanded = false;
  export let ontoggle: () => void = () => {};
  export let dataMode: PublicSnapshot["dataMode"] = "live";

  $: current = bridgeTrafficCopy[bridge.state];
  $: outlook = forecastCopy[forecast.state];
  $: outlookHeadline = forecastHeadline(forecast, outlook.label);
  $: outlookDetail = forecastDetail(forecast, outlook.detail);
  $: weatherView = presentWeather(weather, now);

  function forecastHeadline(
    value: PublicSnapshot["forecast"],
    fallback: string,
  ): string {
    if (value.state !== "likely" || value.openingWindow === null) return fallback;
    const { earliestMinutes, latestMinutes } = value.openingWindow;
    const timing = earliestMinutes === latestMinutes
      ? `T−${earliestMinutes} MIN`
      : `T−${earliestMinutes}–${latestMinutes} MIN`;
    return `${fallback} · ${timing}`;
  }

  function forecastDetail(
    value: PublicSnapshot["forecast"],
    fallback: string,
  ): string {
    if (value.state !== "likely") return fallback;
    let reason: string | null = null;
    if (value.reason?.kind === "vessel_approach") {
      reason = value.reason.vesselName
        ? `${value.reason.vesselName} is approaching Brickell.`
        : "A vessel is approaching Brickell.";
    } else if (value.reason?.kind === "scheduled_river_transit") {
      reason = "A scheduled river transit is approaching Brickell.";
    } else if (value.reason?.kind === "upstream_progress") {
      reason = "A vessel is progressing downstream toward Brickell.";
    }
    const confidence = value.confidencePercent === null
      ? null
      : `${value.confidencePercent}% confidence`;
    if (reason && confidence) return `${reason} ${confidence}.`;
    return reason ?? confidence ?? fallback;
  }

  function presentWeather(
    outlook: PublicSnapshot["weather"],
    clock: Date,
  ): WeatherView {
    const nowMs = clock.getTime();
    const windowEndMs = outlook.windowEndsAt === null
      ? null
      : new Date(outlook.windowEndsAt).getTime();
    const windowExpired = windowEndMs !== null && windowEndMs <= nowMs;

    const temperature = outlook.temperatureF === null
      ? "—°"
      : `${Math.round(outlook.temperatureF)}°`;
    const wind = formatWind(outlook.windSpeedMph, outlook.windDirectionDegrees);
    const condition = conditionLabel(outlook.condition);
    const currentIcon = conditionIcon(outlook.condition, outlook.isDay);
    const chance = outlook.rainChancePercent === null
      ? "CHANCE UNAVAILABLE"
      : `${Math.round(outlook.rainChancePercent)}% CHANCE`;
    const windowStartMs = outlook.windowStartsAt === null
      ? null
      : new Date(outlook.windowStartsAt).getTime();
    const leadMinutes = windowStartMs === null
      ? null
      : Math.max(0, Math.ceil((windowStartMs - nowMs) / 300_000) * 5);

    if (
      !windowExpired &&
      (outlook.state === "rain_window_now" ||
        (outlook.state === "rain_expected" && leadMinutes === 0))
    ) {
      return {
        tone: "active",
        icon: outlook.condition === "thunderstorm" ? "thunderstorm" : "rain",
        temperature,
        status: "RAIN NOW",
        detail: `${chance} · ${wind}`,
        timing: outlook.windowEndsAt === null
          ? null
          : `THROUGH ${formatEtTime(outlook.windowEndsAt).toUpperCase()}`,
        alerting: true,
      };
    }

    if (!windowExpired && outlook.state === "rain_expected") {
      return {
        tone: "expected",
        icon: outlook.condition === "thunderstorm" ? "thunderstorm" : "rain",
        temperature,
        status: leadMinutes === null ? "RAIN EXPECTED" : `RAIN IN T−${leadMinutes} MIN`,
        detail: `${chance} · ${wind}`,
        timing: null,
        alerting: true,
      };
    }

    if (hasCurrentConditions(outlook)) {
      return {
        tone: "current",
        icon: currentIcon,
        temperature,
        status: condition,
        detail: wind,
        timing: null,
        alerting: false,
      };
    }

    return unavailableWeather();
  }

  function unavailableWeather(): WeatherView {
    return {
      tone: "unavailable",
      icon: "unknown",
      temperature: "—°",
      status: "CONDITIONS UNAVAILABLE",
      detail: "WIND UNAVAILABLE",
      timing: null,
      alerting: false,
    };
  }

  function hasCurrentConditions(outlook: PublicSnapshot["weather"]): boolean {
    return outlook.condition !== "unknown" ||
      outlook.temperatureF !== null ||
      outlook.windSpeedMph !== null;
  }

  function conditionLabel(condition: PublicSnapshot["weather"]["condition"]): string {
    const labels: Record<PublicSnapshot["weather"]["condition"], string> = {
      clear: "CLEAR",
      mainly_clear: "MOSTLY CLEAR",
      partly_cloudy: "PARTLY CLOUDY",
      overcast: "OVERCAST",
      fog: "FOG",
      drizzle: "DRIZZLE",
      freezing_drizzle: "FREEZING DRIZZLE",
      rain: "RAIN",
      freezing_rain: "FREEZING RAIN",
      snow: "SNOW",
      showers: "SHOWERS",
      thunderstorm: "THUNDERSTORMS",
      unknown: "CONDITIONS UNAVAILABLE",
    };
    return labels[condition];
  }

  function conditionIcon(
    condition: PublicSnapshot["weather"]["condition"],
    isDay: boolean | null,
  ): WeatherIcon {
    if (condition === "clear") return isDay === false ? "clear-night" : "clear-day";
    if (condition === "mainly_clear" || condition === "partly_cloudy") {
      return isDay === false ? "partly-cloudy-night" : "partly-cloudy-day";
    }
    if (condition === "overcast") return "cloudy";
    if (condition === "fog") return "fog";
    if (condition === "drizzle" || condition === "freezing_drizzle") return "drizzle";
    if (condition === "rain" || condition === "freezing_rain" || condition === "showers") {
      return "rain";
    }
    if (condition === "snow") return "snow";
    if (condition === "thunderstorm") return "thunderstorm";
    return "unknown";
  }

  function formatWind(speedMph: number | null, directionDegrees: number | null): string {
    if (speedMph === null) return "WIND UNAVAILABLE";
    const direction = directionDegrees === null ? "" : ` ${compassPoint(directionDegrees)}`;
    return `WIND${direction} ${Math.round(speedMph)} MPH`;
  }

  function compassPoint(degrees: number): string {
    const points = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
    return points[Math.round((((degrees % 360) + 360) % 360) / 45) % points.length]!;
  }
</script>

<section class:attention={forecast.state === "likely" || bridge.state === "up"} class="forecast" aria-labelledby="forecast-title">
  <div class="forecast__summary">
    <h1 id="forecast-title">{current.label}</h1>
    <p class="forecast__consequence">{current.consequence}</p>
    {#if compact}
      <button class="forecast__toggle" type="button" aria-label={expanded ? "Hide forecast and weather" : "Show forecast and weather"} aria-expanded={expanded} aria-controls="forecast-details" onclick={ontoggle}>
        {expanded ? "Less" : "Details"}
        {#if expanded}<ChevronUp size={18} aria-hidden="true" />{:else}<ChevronDown size={18} aria-hidden="true" />{/if}
      </button>
      <p class="forecast__glance"><span>Brickell · {outlookHeadline}</span><span>{weatherView.temperature}</span></p>
    {/if}
  </div>
  <div id="forecast-details" class="forecast__body" hidden={compact && !expanded}>
    <div class="forecast__bridge">
      <p class="forecast__detail"><strong>{outlookHeadline}</strong><span>{outlookDetail}</span></p>
      <div class="forecast__meta">
        <div class="forecast__meta-status">
          <span>Brickell outlook · next 30 minutes</span>
          <span class="forecast__freshness">{freshnessLabel(forecast.freshness, dataMode)}</span>
          <span>Updated {formatEtTime(forecast.updatedAt)}</span>
          <span class="forecast__surface-traffic">{brickellAvenueTrafficLabel(roadTraffic)}</span>
        </div>
      </div>
    </div>

    <aside
      class={`forecast__weather forecast__weather--${weatherView.tone}`}
      data-weather-state={weatherView.tone}
      data-weather-icon={weatherView.icon}
      aria-label="Current weather and rain forecast at Brickell Bridge"
      aria-live={weatherView.alerting ? "polite" : "off"}
      aria-atomic="true"
    >
      <div class="forecast__weather-glyph" aria-hidden="true">
        {#if weatherView.icon === "clear-day"}
          <Sun size={34} strokeWidth={1.8} />
        {:else if weatherView.icon === "clear-night"}
          <Moon size={34} strokeWidth={1.8} />
        {:else if weatherView.icon === "partly-cloudy-day"}
          <CloudSun size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "partly-cloudy-night"}
          <CloudMoon size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "cloudy"}
          <Cloudy size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "fog"}
          <CloudFog size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "drizzle"}
          <CloudDrizzle size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "rain"}
          <CloudRain size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "snow"}
          <CloudSnow size={36} strokeWidth={1.8} />
        {:else if weatherView.icon === "thunderstorm"}
          <CloudLightning size={36} strokeWidth={1.8} />
        {:else}
          <Cloud size={34} strokeWidth={1.8} />
        {/if}
      </div>
      <div class="forecast__weather-content">
        <div class="forecast__weather-topline">
          <span class="forecast__weather-kicker">BRICKELL WEATHER</span>
        </div>
        <div class="forecast__weather-reading">
          <strong class="forecast__weather-temperature">{weatherView.temperature}</strong>
          <div class="forecast__weather-status">
            <strong>{weatherView.status}</strong>
            <span>{weatherView.detail}</span>
          </div>
        </div>
        {#if weatherView.timing}<div class="forecast__weather-timing">{weatherView.timing}</div>{/if}
      </div>
    </aside>
  </div>
</section>
